import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import type {
  ReportShareEntry,
  ReportShareInput,
  ReportShareLevel,
  ReportSharesResponse
} from "../types/collab.types.ts"

export type ShareEditorKind = "user" | "role"

export interface ShareEditorEntry {
  key: string
  kind: ShareEditorKind
  targetId: number | string
  name: string
  level: ReportShareLevel
}

const entryKey = (kind: ShareEditorKind, id: number | string): string =>
  `${kind}:${id}`

export const sharesToEditorEntries = (
  shares: ReportShareEntry[]
): ShareEditorEntry[] =>
  shares
    .map((share): ShareEditorEntry | null => {
      if (share.user) {
        return {
          key: entryKey("user", share.user.id),
          kind: "user",
          targetId: share.user.id,
          name: getEntityName(share.user),
          level: share.level
        }
      }
      if (share.role) {
        return {
          key: entryKey("role", share.role.id),
          kind: "role",
          targetId: share.role.id,
          name: share.role.name,
          level: share.level
        }
      }
      return null
    })
    .filter((entry): entry is ShareEditorEntry => entry !== null)

export const makeUserEditorEntry = (
  userId: number | string,
  name: string,
  level: ReportShareLevel = "view"
): ShareEditorEntry => ({
  key: entryKey("user", userId),
  kind: "user",
  targetId: userId,
  name,
  level
})

export const makeRoleEditorEntry = (
  roleId: number | string,
  name: string,
  level: ReportShareLevel = "view"
): ShareEditorEntry => ({
  key: entryKey("role", roleId),
  kind: "role",
  targetId: roleId,
  name,
  level
})

export const editorEntriesToPayload = (
  entries: ShareEditorEntry[]
): ReportShareInput[] =>
  entries.map((entry) =>
    entry.kind === "user"
      ? { user_id: entry.targetId, level: entry.level }
      : { role_id: entry.targetId, level: entry.level }
  )

export const sharedUserIds = (entries: ShareEditorEntry[]): Set<string> =>
  new Set(
    entries
      .filter((entry) => entry.kind === "user")
      .map((entry) => String(entry.targetId))
  )

export const sharedRoleIds = (entries: ShareEditorEntry[]): Set<string> =>
  new Set(
    entries
      .filter((entry) => entry.kind === "role")
      .map((entry) => String(entry.targetId))
  )

export const shareSaveErrorMessage = (response: {
  data?: { details?: { message?: string | null } }
}): string | undefined => {
  const message = response.data?.details?.message
  return typeof message === "string" && message.trim() ? message : undefined
}

export const initialSharesResponse: ReportSharesResponse = {
  visibility: "private",
  shares: []
}
